import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Minus, Plus } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { Skeleton } from '../../../shared/components/Skeleton';
import { useVersionDiff } from '../hooks';

/**
 * Comparação estruturada entre duas versões (US-COM-006, task 15.5).
 * Consome `GET /proposals/:id/versions/:vA/diff/:vB` do backend (task
 * 5.3). Reaproveita o formato do `CommercialBaseVersionDiff`.
 */
export default function PropostaVersaoComparacaoPage(): React.ReactElement {
  const navigate = useNavigate();
  const { id, vA, vB } = useParams<{ id: string; vA: string; vB: string }>();
  const { data, isLoading, isError, error } = useVersionDiff(id, vA, vB);

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (isError || !data)
    return (
      <div className="p-4">
        <Alert variant="error">
          Erro ao carregar diff: {(error as Error)?.message ?? 'versões inválidas'}
        </Alert>
      </div>
    );

  return (
    <div className="p-4 sm:p-6">
      <button
        type="button"
        onClick={() => navigate(`/propostas/${id}/versoes`)}
        className="mb-4 inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Voltar
      </button>

      <h1 className="mb-2 text-xl font-semibold text-gray-900">Comparação de versões</h1>
      <div className="mb-6 flex items-center gap-2 text-sm text-gray-600">
        <span className="rounded bg-gray-100 px-2 py-0.5 font-mono">v{data.from.versionNumber}</span>
        <ArrowRight className="h-4 w-4" />
        <span className="rounded bg-gray-100 px-2 py-0.5 font-mono">v{data.to.versionNumber}</span>
      </div>

      <section className="mb-6 rounded border border-gray-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-medium text-gray-900">Campos alterados</h3>
        {data.changedFields.length === 0 ? (
          <p className="text-sm text-gray-500">Nenhum campo alterado nas colunas comparadas.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase text-gray-500">
                <th className="pb-2">Campo</th>
                <th className="pb-2">Antes</th>
                <th className="pb-2">Depois</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.changedFields.map((c) => (
                <tr key={c.field}>
                  <td className="py-2 font-medium text-gray-900">{c.field}</td>
                  <td className="py-2 text-gray-700">{formatValue(c.before)}</td>
                  <td className="py-2 text-gray-900">{formatValue(c.after)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="rounded border border-gray-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-medium text-gray-900">Itens</h3>
        <div className="flex flex-wrap gap-4 text-sm">
          <span className="inline-flex items-center gap-1 text-emerald-700">
            <Plus className="h-4 w-4" />
            {data.itemsAdded} adicionado(s)
          </span>
          <span className="inline-flex items-center gap-1 text-red-700">
            <Minus className="h-4 w-4" />
            {data.itemsRemoved} removido(s)
          </span>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Diff por chave composta (serviço+perfil+valor+quantidade). Alterações de valor num
          mesmo item aparecem como remoção + adição.
        </p>
      </section>
    </div>
  );
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

export { PropostaVersaoComparacaoPage };
